import apiClient from '../../lib/axios';

/**
 * Upload file. multipart/form-data: file, folderId; optional title, documentTypeId, metadata.
 * @param {FormData} formData - Must include 'file' and 'folderId'; optional title, documentTypeId, metadata
 * @returns {Promise<{ data?: object }>}
 */
export const uploadDocument = async (formData) => {
  const { data } = await apiClient.post('/api/documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

/**
 * Upload document with optional folder (temporary filing).
 * POST /api/documents/upload-temporary
 * Accepts: optional folderId, optional documentTypeId, optional workflowGroupId, optional repository (default NEW_ARCHIVE), optional title, optional metadata.
 * Use for filing workflow where document may be filed later.
 * @param {FormData} formData - Must include 'file'; optional folderId, documentTypeId, workflowGroupId, repository, title, metadata
 * @returns {Promise<{ data?: object }>}
 */
export const uploadDocumentTemporary = async (formData) => {
  const { data } = await apiClient.post('/api/documents/upload-temporary', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

/**
 * Document metadata by ID.
 * @param {string} id - Document ID
 */
export const getDocument = async (id) => {
  const { data } = await apiClient.get(`/api/documents/${id}`);
  return data;
};

/**
 * Get view URL for preview/open in new tab.
 * @param {string} id - Document ID
 */
export const getDocumentUrl = async (id) => {
  const { data } = await apiClient.get(`/api/documents/${id}/url`);
  return data;
};

/**
 * Stream file inline (e.g. iframe preview). Returns blob/stream URL or response.
 * @param {string} id - Document ID
 * @param {Object} [options] - e.g. { responseType: 'blob' } if needed
 */
export const getDocumentView = async (id, options = {}) => {
  const { data } = await apiClient.get(`/api/documents/${id}/view`, options);
  return data;
};

/**
 * Download file.
 * @param {string} id - Document ID
 * @param {Object} [options] - e.g. { responseType: 'blob' } for file download
 */
export const getDocumentDownload = async (id, options = {}) => {
  const response = await apiClient.get(`/api/documents/${id}/download`, {
    ...options,
    responseType: options.responseType ?? 'blob',
  });
  return response;
};

/**
 * Update document metadata. Payload may include title, originalName, documentTypeId.
 * @param {string} id - Document ID
 * @param {Object} payload
 */
export const updateDocument = async (id, payload) => {
  const { data } = await apiClient.patch(`/api/documents/${id}`, payload);
  return data;
};

/**
 * Update document type, title, and/or move document to a new folder (file document).
 * PATCH /api/documents/{id}/file
 * When folderId is provided and different, the file is moved on disk.
 * @param {string} id - Document ID
 * @param {Object} payload - Optional title, documentTypeId, folderId
 * @returns {Promise<{ data?: object }>}
 */
export const fileDocument = async (id, payload) => {
  const { data } = await apiClient.patch(`/api/documents/${id}/file`, payload);
  return data;
};

/**
 * Delete document.
 * @param {string} id - Document ID
 */
export const deleteDocument = async (id) => {
  const { data } = await apiClient.delete(`/api/documents/${id}`);
  return data;
};

/**
 * Get all documents in the Temporary folder (unfiled).
 * GET /api/documents/temporary
 * @returns {Promise<{ data?: array, documents?: array }>}
 */
export const getTemporaryDocuments = async () => {
  const { data } = await apiClient.get('/api/documents/temporary');
  return data;
};

/**
 * Search documents. Supports pagination.
 * @param {Object} params - q, repository, folderId, documentTypeId, unfiled, page, limit
 * @param {boolean} [params.unfiled] - If true, return documents with no folder (for filing list)
 * @returns {Promise<{ data?: array, documents?: array, pagination?: { page, limit, total, pages } }>}
 */
export const searchDocuments = async (params = {}) => {
  const searchParams = new URLSearchParams();
  if (params.q) searchParams.set('q', params.q);
  if (params.repository) searchParams.set('repository', params.repository);
  if (params.folderId) searchParams.set('folderId', params.folderId);
  if (params.documentTypeId) searchParams.set('documentTypeId', params.documentTypeId);
  if (params.unfiled === true) searchParams.set('unfiled', 'true');
  if (params.page != null) searchParams.set('page', String(params.page));
  if (params.limit != null) searchParams.set('limit', String(params.limit));
  const { data } = await apiClient.get(`/api/documents/search?${searchParams.toString()}`);
  return data;
};

/**
 * Get documents by creator (for profile: "my documents").
 * @param {Object} params
 * @param {string} params.createdBy - User ID (creator)
 * @param {number} [params.page=1]
 * @param {number} [params.limit=10]
 * @param {string} [params.sortBy=createdAt]
 * @param {string} [params.sortOrder=desc]
 * @returns {Promise<{ data: { documents: array, pagination: object } }>}
 */
export const getDocumentsByCreator = async (params = {}) => {
  const searchParams = new URLSearchParams();
  if (params.createdBy) searchParams.set('createdBy', params.createdBy);
  if (params.page != null) searchParams.set('page', String(params.page));
  if (params.limit != null) searchParams.set('limit', String(params.limit));
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);
  const { data } = await apiClient.get(`/api/documents/by-creator?${searchParams.toString()}`);
  return data;
};

export default uploadDocument;
